import React from 'react';
import { getGridStringOperators, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ActionsCell, ServiceCell, StatusCell } from '@warehouse/object-browser/feature-broker';
import { BrokerJob, BrokerService } from '@warehouse/object-browser/feature-broker-core';
import { timeToString } from '@warehouse/shared/legacy-ui';
import { getCustomGridSingleSelectOperators, OUTLINED_LINK_CELL_CLASS_NAME } from '@warehouse/shared/ui';
import {
	ActionsColumnName,
	BucketColumnName,
	DurationColumnName,
	ObjectColumnName,
	EndColumnName,
	formatDuration,
	jobDurationMs,
	JobIdColumnName,
	SERVICE_LOG_STATUS_QUICK_FILTERS,
	ServiceColumnName,
	StartColumnName,
	StatusColumnName,
	TriggeredByColumnName,
} from '@warehouse/service-log/core';
import { BucketCell, JobIdCell, ObjectCell } from './fields';

/** What the Broker can do on `triggeredByDisplayName` (keyword field): equals / isAnyOf → equals, startsWith → startsWith. */
export const TRIGGERED_BY_FILTER_OPERATORS = ['equals', 'startsWith', 'isAnyOf'];

/** The status chips minus All: the column filter offers the same statuses, with the same labels. */
const STATUS_OPTIONS = SERVICE_LOG_STATUS_QUICK_FILTERS.filter(({ value }) => value !== 'ALL');

interface ColumnsOptions {
	/** Active catalog services, offered as the Service column's filter options. */
	services: BrokerService[];
	onJobCancelled: (jobId: string) => void;
}

/**
 * Same cells as the Object Browser Logs tab (ServiceCell, StatusCell, ActionsCell), plus Object and Bucket resolved
 * from the job id through the Inventory (ObjectCell, BucketCell).
 * Object / Bucket / Start / End / Duration have no Broker filter, so they are not filterable. Nothing sorts (no Broker sort), like the Logs tab.
 */
export function createServiceLogColumns({ services, onJobCancelled }: ColumnsOptions): GridColDef<BrokerJob>[] {
	return [
		{
			field: ServiceColumnName,
			headerName: 'Service',
			width: 240,
			minWidth: 200,
			type: 'singleSelect',
			valueOptions: services.map((service) => ({ value: service.serviceName, label: service.serviceName })),
			filterOperators: getCustomGridSingleSelectOperators(),
			renderCell: ServiceCell,
			valueGetter: (_, row) => row.serviceName,
		},
		{
			field: ObjectColumnName,
			headerName: 'ETag',
			flex: 1,
			minWidth: 260,
			filterable: false,
			cellClassName: OUTLINED_LINK_CELL_CLASS_NAME,
			renderCell: ObjectCell,
			valueGetter: (_, row) => row.jobId,
		},
		{
			field: BucketColumnName,
			headerName: 'Bucket',
			width: 230,
			filterable: false,
			renderCell: BucketCell,
			valueGetter: (_, row) => row.jobId,
		},
		{
			field: StatusColumnName,
			headerName: 'Status',
			width: 168,
			type: 'singleSelect',
			valueOptions: STATUS_OPTIONS,
			filterOperators: getCustomGridSingleSelectOperators(),
			renderCell: StatusCell,
			valueGetter: (_, row) => row.status,
		},
		{
			field: TriggeredByColumnName,
			headerName: 'Triggered by',
			width: 180,
			type: 'string',
			// The Broker indexes the caller name as a keyword: only exact match and prefix exist, no substring.
			filterOperators: getGridStringOperators().filter((operator) =>
				TRIGGERED_BY_FILTER_OPERATORS.includes(operator.value),
			),
			// Shows exactly what the filter searches (the display name): a job without one shows --, never its raw
			// `triggeredBy`, which a Triggered by filter could not find.
			valueGetter: (_, row) => row.triggeredByDisplayName ?? null,
			valueFormatter: (value: string | null) => value ?? '--',
		},
		{
			field: StartColumnName,
			headerName: 'Start',
			width: 165,
			filterable: false,
			valueGetter: (_, row) => row.startedAt,
			valueFormatter: (_, row) => timeToString(new Date(row.startedAt)),
		},
		{
			field: EndColumnName,
			headerName: 'End',
			width: 165,
			filterable: false,
			valueGetter: (_, row) => row.closedAt,
			valueFormatter: (_, row) => (row.closedAt ? timeToString(new Date(row.closedAt)) : '--'),
		},
		{
			field: DurationColumnName,
			headerName: 'Duration',
			width: 120,
			filterable: false,
			valueGetter: (_, row) => jobDurationMs(row),
			valueFormatter: (_, row) => formatDuration(jobDurationMs(row)),
		},
		{
			field: JobIdColumnName,
			headerName: 'Job ID',
			width: 300,
			filterable: false,
			renderCell: JobIdCell,
			valueGetter: (_, row) => row.jobId,
		},
		{
			field: ActionsColumnName,
			headerName: '',
			width: 60,
			align: 'center',
			sortable: false,
			filterable: false,
			hideable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<BrokerJob, string>) => (
				<ActionsCell job={params.row} onCancelled={onJobCancelled} />
			),
		},
	];
}
